import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SignedIn } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { useCart } from '../contexts/CartContext';
import ImageWithPlaceholder from '../components/ImageWithPlaceholder';
import AuthenticVendorBadge from '../components/AuthenticVendorBadge';

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [item, setItem] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [addedFlash, setAddedFlash] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/items/${id}`, {});
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setItem(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const handleCarouselNext = () => {
    if (!item?.imageUrls?.length) return;
    setCurrentImageIndex((prev) => (prev < item.imageUrls.length - 1 ? prev + 1 : 0));
  };

  const handleCarouselPrev = () => {
    if (!item?.imageUrls?.length) return;
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : item.imageUrls.length - 1));
  };

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    addItem(id, 1);
    setAddedFlash(true);
    window.setTimeout(() => setAddedFlash(false), 1800);
  };

  const renderStars = (stars) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < stars ? 'text-amber-400' : 'text-slate-300 dark:text-slate-600'}>
        ★
      </span>
    ));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-2xl text-slate-500 dark:text-slate-400">Loading item…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Item</h1>
        <p className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 text-lg">{error}</p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-semibold">Item</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">Item not found.</p>
      </div>
    );
  }

  const imageUrls = item.imageUrls || [];
  const isOutOfStock = item.stockCount <= 0;
  const hasImages = imageUrls.length > 0;
  const marketLabel = item.marketName || 'the flea market';

  return (
    <div className="flex flex-col gap-5 pb-bottom-nav-action max-w-screen-md mx-auto w-full">
      {item.vendorId != null && (
        <button
          type="button"
          onClick={() => navigate(`/vendor/${item.vendorId}`)}
          className="self-start inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
        >
          <span aria-hidden>←</span>
          {item.vendorName ? `Back to ${item.vendorName}` : 'Back to vendor'}
        </button>
      )}
      {/* Hero image */}
      <div className="relative w-full aspect-square bg-slate-100 dark:bg-slate-900 rounded-2xl overflow-hidden -mt-1">
        {hasImages ? (
          <>
            <ImageWithPlaceholder
              src={imageUrls[currentImageIndex]}
              alt={item.title}
              className="w-full h-full object-cover"
              containerClassName="absolute inset-0"
            />
            {imageUrls.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handleCarouselPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/55 text-white flex items-center justify-center text-2xl"
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={handleCarouselNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/55 text-white flex items-center justify-center text-2xl"
                  aria-label="Next image"
                >
                  ›
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {imageUrls.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2.5 h-2.5 rounded-full ${
                        idx === currentImageIndex ? 'bg-white' : 'bg-white/45'
                      }`}
                      aria-label={`Image ${idx + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400 text-lg">No photo yet</div>
        )}
      </div>

      {/* Title + price */}
      <div className="flex flex-col items-center text-center gap-3 px-0.5">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-50 leading-tight">
          {item.title}
        </h1>

        <div className="flex flex-col items-center gap-2">
          <p className="text-4xl font-bold text-[#E47911]">${parseFloat(item.price).toFixed(2)}</p>
          {isOutOfStock ? (
            <span className="px-3 py-1.5 rounded-full bg-red-100 text-red-800 text-sm font-semibold">Out of stock</span>
          ) : (
            <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold">
              In stock · {item.stockCount} left
            </span>
          )}
        </div>
      </div>

      {item.isVerifiedVendor && (
        <AuthenticVendorBadge
          marketName={item.marketName}
          vendorName={item.vendorName}
          vendorBooth={item.vendorBooth}
        />
      )}

      {/* Pickup / how it works — one clear block for customers */}
      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-1.5">
        <p className="font-semibold text-slate-900 dark:text-slate-100">How pickup works</p>
        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
          Pay securely in the app, then pick up at{' '}
          <span className="font-medium text-slate-800 dark:text-slate-100">{marketLabel}</span>
          {item.vendorBooth ? (
            <>
              {' '}
              · Booth <span className="font-medium">{item.vendorBooth}</span>
            </>
          ) : null}
          {item.vendorName ? (
            <>
              {' '}
              from{' '}
              <button
                type="button"
                onClick={() => navigate(`/vendor/${item.vendorId}`)}
                className="font-medium text-[#E47911] hover:underline"
              >
                {item.vendorName}
              </button>
            </>
          ) : null}
          . After pay, open My Purchases and show the QR to the vendor. No off-app payments.
        </p>
      </div>

      {item.description && (
        <div className="flex flex-col gap-2 pt-1">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">About this item</h2>
          <p className="text-base text-slate-700 dark:text-slate-300 whitespace-pre-line leading-relaxed">
            {item.description}
          </p>
        </div>
      )}

      {/* Reviews */}
      <div className="flex flex-col gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">Reviews</h2>
          <SignedIn>
            <button
              type="button"
              onClick={() => navigate(`/review/${id}?type=item`)}
              className="text-sm font-medium text-[#E47911] hover:underline"
            >
              Write a review
            </button>
          </SignedIn>
        </div>

        {item.reviewCount === 0 ? (
          <p className="text-slate-500 dark:text-slate-400 text-sm">No reviews yet.</p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold">{(item.averageStars || 0).toFixed(1)}</span>
              <div className="flex flex-col gap-0.5">
                <div className="flex">{renderStars(Math.round(item.averageStars || 0))}</div>
                <span className="text-xs text-slate-500">
                  {item.reviewCount} {item.reviewCount === 1 ? 'review' : 'reviews'}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setReviewsExpanded(!reviewsExpanded)}
              className="text-left text-sm font-medium text-slate-700 dark:text-slate-300 hover:underline"
            >
              {reviewsExpanded ? 'Hide reviews' : `Show reviews (${item.reviewCount})`}
            </button>
            {reviewsExpanded && (
              <div className="flex flex-col gap-3">
                {item.reviews.map((review) => (
                  <div
                    key={review.id}
                    className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex">{renderStars(review.stars)}</div>
                      <span className="text-xs text-slate-500">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">{review.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Customer buy bar — flush on top of BottomNav (no gap) */}
      <div
        className="fixed left-0 right-0 z-40 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3"
        style={{ bottom: 'var(--app-bottom-nav-height, 3.5rem)' }}
      >
        <div className="max-w-screen-md mx-auto">
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={isOutOfStock}
            className={`w-full py-4 rounded-xl text-xl font-semibold transition-colors ${
              isOutOfStock
                ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'
                : addedFlash
                  ? 'bg-emerald-600 text-white'
                  : 'bg-[#E47911] text-white hover:bg-[#C96A0C] active:bg-[#A85A0A]'
            }`}
          >
            {isOutOfStock ? 'Out of stock' : addedFlash ? 'Added to cart' : 'Add to cart'}
          </button>
        </div>
      </div>
    </div>
  );
}
